import React, { useState } from 'react';

const VonScalperFAQ = () => {
  const [activeQuestion, setActiveQuestion] = useState(null);

  const faqs = [
    {
      category: "General",
      questions: [
        {
          question: "What markets can VON SCALPER trade?",
          answer: "VON SCALPER is designed for universal compatibility across Forex majors and minors, all major cryptocurrencies (Bitcoin, Ethereum, etc.), and V75 Synthetic Indices. The system automatically detects the symbol type and optimizes parameters accordingly for each market."
        },
        {
          question: "What platform does VON SCALPER work with?",
          answer: "VON SCALPER is exclusively designed for MetaTrader 5 (MT5). It requires MT5 build 3200 or higher and is compatible with all major brokers that support MT5, including ECN, STP, and market maker brokers."
        },
        {
          question: "Do I need trading experience to use VON SCALPER?",
          answer: "While VON SCALPER is fully automated, basic understanding of trading concepts is recommended. The system includes comprehensive documentation, setup guides, and we offer optional training sessions to help you understand the strategy and optimize settings for your risk tolerance."
        }
      ]
    },
    {
      category: "Technical",
      questions: [
        {
          question: "How does the multi-timeframe analysis work?",
          answer: "VON SCALPER analyzes three timeframes simultaneously: 15M for trend bias detection, 5M for pattern confirmation and zone identification, and 1M for precise entry timing. All three timeframes must align before a trade is executed, ensuring high-quality setups."
        },
        {
          question: "What ICT patterns does the system recognize?",
          answer: "The system detects 10+ institutional patterns including Fair Value Gaps (FVG), Order Blocks, Breaker Blocks, BPR zones, DR Liquidity, SMT Divergence, Change of Character (CHoCH), and Judas Swings. Each pattern goes through multiple validation criteria before triggering a trade."
        },
        {
          question: "How does the adaptive ML framework work?",
          answer: "The ML system continuously analyzes market conditions and trading performance to optimize parameters in real-time. It classifies markets into 6 regimes (trending strong/weak, ranging tight/wide, volatile, quiet) and adjusts risk management, position sizing, and entry criteria accordingly."
        }
      ]
    },
    {
      category: "Risk & Performance",
      questions: [
        {
          question: "How does VON SCALPER manage risk?",
          answer: "The system employs multiple risk layers: daily loss caps, equity lock levels, position limits (separate for BUY/SELL), dynamic stop losses, intelligent breakeven management, and adaptive trailing stops. You can configure fixed dollar risk amounts or percentage-based risk per trade."
        },
        {
          question: "What's the typical drawdown and performance?",
          answer: "Based on backtesting, maximum drawdown typically stays under 10-12% with proper risk settings. Performance varies by market conditions, but the system targets 15-25% monthly returns with a Sharpe ratio above 1.5. All performance metrics are based on simulated results."
        },
        {
          question: "Can I use this with prop trading accounts?",
          answer: "Yes, VON SCALPER is designed with prop trading rules in mind. The risk management system can be configured to stay within strict drawdown limits, and the system includes features specifically for funded account compliance like daily loss limits and conservative position sizing."
        }
      ]
    },
    {
      category: "Setup & Support",
      questions: [
        {
          question: "How difficult is the setup process?",
          answer: "Setup is straightforward and typically takes 15-20 minutes. You'll receive step-by-step video instructions, and our support team can assist with installation. The system includes an auto-configuration feature that detects your broker and symbol characteristics."
        },
        {
          question: "What kind of support do you provide?",
          answer: "We offer comprehensive support including email assistance, live chat during business hours, detailed documentation, video tutorials, and optional 1-on-1 setup sessions. Professional and Enterprise plans include priority support with faster response times."
        },
        {
          question: "Does VON SCALPER require constant monitoring?",
          answer: "No, VON SCALPER is fully automated. Once configured, it runs independently and includes Telegram notifications to keep you informed of trades and system status. The system also has built-in safety mechanisms and automatic error recovery."
        }
      ]
    },
    {
      category: "Licensing & Updates",
      questions: [
        {
          question: "How does the licensing work?",
          answer: "Each license is tied to one MT5 account number. You can change the account number if needed, but the license cannot be used simultaneously on multiple accounts. Professional plans include multiple licenses for portfolio management."
        },
        {
          question: "Are updates included?",
          answer: "Yes, all plans include regular updates with new features, optimizations, and market adaptations. Lifetime plans receive updates permanently, while monthly plans receive updates during their subscription period."
        },
        {
          question: "What's your refund policy?",
          answer: "We offer a comprehensive 30-day money-back guarantee. If you're not satisfied with VON SCALPER's performance or it doesn't meet your expectations, we'll provide a full refund with no questions asked."
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const questionId = `${categoryIndex}-${questionIndex}`;
    setActiveQuestion(activeQuestion === questionId ? null : questionId);
  };

  return (
    <section className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white" id="faq">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="h-0.5 w-12 bg-gradient-to-r from-cyan-400 to-purple-400 mr-4"></div>
            <span className="py-2 px-4 rounded-full text-cyan-300 bg-cyan-900/60 font-semibold text-sm border border-cyan-700">FAQ</span>
            <div className="h-0.5 w-12 bg-gradient-to-r from-purple-400 to-cyan-400 ml-4"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Everything you need to know about VON SCALPER's features, setup, and performance.
          </p>
        </div>

        {/* FAQ Categories */}
        <div className="max-w-4xl mx-auto space-y-12">
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex} className="space-y-6">
              {/* Category Header */}
              <div className="border-b border-slate-700 pb-4">
                <h3 className="text-2xl font-bold text-cyan-400">{category.category}</h3>
              </div>

              {/* Questions */}
              <div className="space-y-4">
                {category.questions.map((faq, questionIndex) => {
                  const questionId = `${categoryIndex}-${questionIndex}`;
                  const isActive = activeQuestion === questionId;

                  return (
                    <div
                      key={questionIndex}
                      className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 overflow-hidden transition-all duration-300 hover:border-slate-600"
                    >
                      <button
                        onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                        className="w-full px-6 py-5 text-left flex items-center justify-between focus:outline-none group"
                      >
                        <span className="text-lg font-semibold text-white group-hover:text-cyan-300 transition-colors pr-4">
                          {faq.question}
                        </span>
                        <svg
                          className={`w-6 h-6 text-slate-400 group-hover:text-cyan-300 transition-all duration-300 flex-shrink-0 ${
                            isActive ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      <div
                        className={`transition-all duration-300 ease-in-out ${
                          isActive ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        } overflow-hidden`}
                      >
                        <div className="px-6 pb-5">
                          <p className="text-slate-300 leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Support */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-slate-800/50 to-purple-800/50 rounded-2xl p-8 backdrop-blur-sm border border-slate-700 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4 text-white">Still Have Questions?</h3>
            <p className="text-slate-300 mb-6 leading-relaxed">
              Our technical support team is ready to help you get the most out of VON SCALPER. 
              Contact us for personalized assistance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              
              <a
                href="mailto:hello@ksquants.com"
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-cyan-600 to-purple-600 text-white font-semibold rounded-xl hover:from-cyan-500 hover:to-purple-500 transition-all shadow-lg"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                Email Support
              </a>
              
              <a
                href="https://wa.me/+2347026976893"
                className="inline-flex items-center justify-center px-6 py-3 bg-slate-700/50 text-slate-200 font-semibold rounded-xl border border-slate-600 hover:bg-slate-600/50 transition-all"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Live Chat
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VonScalperFAQ;